import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { FormattedMessage } from 'react-intl';
import { useFeatureFlag } from 'hellospa/components/feature-flags';
import * as types from 'hellospa/page/prep-and-send/data/types';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import DropboxFilePicker from 'hellospa/components/upload-integrations/dropbox';
import GoogleDriveFilePicker from 'hellospa/components/upload-integrations/google-drive';
import OneDriveFilePicker from 'hellospa/components/upload-integrations/one-drive';
import BoxFilePicker from 'hellospa/components/upload-integrations/box';
import EvernoteFilePicker from 'hellospa/components/upload-integrations/evernote';
import { SelectTemplateModal } from 'hellospa/components/select-template-modal/select-template-modal';
import TemplatePicker from 'hellospa/components/prep-and-send/template-picker';
import InlineLayout from './inline-layout';
import MenuLayout from './menu-layout';
import { usePageProps } from 'hellospa/slices/page-props';
import { ActionSource, setTab, TemplatePageTab } from 'hellospa/page/templates/template-slice';

type Props = {
  onFilesDropped?: () => Promise<void>,
  displayType: types.DropzoneDisplayTypes,
  isFirstTimeExperience?: boolean,
  hideMenuLayoutSignerGraphic?: boolean,
}

type IntegrationState = {
  [key in types.UploadIntegrationType]: boolean
}

const Dropzone: React.FunctionComponent<Props> = ({
  onFilesDropped,
  displayType,
  isFirstTimeExperience,
  hideMenuLayoutSignerGraphic,
}) => {
  const dispatch = useDispatch();
  const allowUpload = useSelector(selectors.allowUpload);
  const allowTemplate = useSelector(selectors.allowTemplate);
  const integrations = useSelector(selectors.getUploadIntegrations);
  const allowIntegrations = useSelector(selectors.allowIntegrationsUpload);
  const isBulkSend = useSelector(selectors.isBulkSend);
  const allowBulkSend = useSelector(selectors.allowBulkSend);
  const userCanCreateEditTemplates = useSelector(selectors.canCreateEditTemplate);
  const filter = useSelector(selectors.getTemplateFilter);
  const isPaidAccount = useSelector(selectors.isPaidAccount);

  const isTemplateRedesignP2 = useFeatureFlag('HelloSign_Stars_20210811_Template_Page_Redesign_P2');
  const isTemplateRedesignModalExperiment = useFeatureFlag('HelloSign_Stars_20211007_Template_Complete_Modal_Redesign');

  const showRedesignedModal = isTemplateRedesignP2
    && isTemplateRedesignModalExperiment
    && !isBulkSend;

  const [uploadIntegration, setUploadIntegration] = React.useState<IntegrationState>({
    [types.UploadIntegrationType.Dropbox]: false,
    [types.UploadIntegrationType.Evernote]: false,
    [types.UploadIntegrationType.OneDrive]: false,
    [types.UploadIntegrationType.GoogleDrive]: false,
    [types.UploadIntegrationType.Box]: false,
  });
  const pageProps = usePageProps('prepandsend');
  let defaultTab: TemplatePageTab = pageProps?.hasStarredTemplates
    ? TemplatePageTab.Star
    : TemplatePageTab.All;
  if (defaultTab !== TemplatePageTab.Star && defaultTab !== TemplatePageTab.All) {
    defaultTab = TemplatePageTab.All;
  }

  React.useEffect(() => {
    dispatch(setTab({ tab: defaultTab, isFree: !isPaidAccount, source: ActionSource.System }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [templatePickerStatus, setTemplatePickerStatus] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);

  const makeToggleTemplatePickerCallbackFunction = React.useCallback(
    (status: boolean) => (): void => {
      setTemplatePickerStatus(status);
    }, [],
  );

  const toggleIntegrationPicker = React.useCallback((
    integration: string,
    status: boolean,
  ) => {
    setUploadIntegration((state) => ({
      ...state,
      [integration]: status,
    }));
  }, []);

  const onSelectTemplate = React.useCallback(async (template: Pick<types.Template, 'id'>) => {
    if (typeof onFilesDropped === 'function') {
      await onFilesDropped();
    }
    dispatch(actions.useTemplate(template.id, isBulkSend));
    setTemplatePickerStatus(false);
  }, [onFilesDropped, dispatch, isBulkSend]);

  const onDrop = React.useCallback(async (files: File[]) => {
    setIsUploading(true);
    if (typeof onFilesDropped === 'function') {
      await onFilesDropped();
    }

    try {
      for (let i = 0; i < files.length; i++) {
        // skipping await here, because this needs to be done concurrently
        // so the user doesn't see 1 file being uploaded at a time
        dispatch(actions.uploadFile(files[i]));
      }
    } catch (e) {
      throw new Error('Failed uploading files!');
    }
  }, [onFilesDropped, dispatch]);

  const onSelectExternalFile = React.useCallback(async (
    type: any,
    fileReference: string,
    name: string,
  ) => {
    if (typeof onFilesDropped === 'function') {
      await onFilesDropped();
    }

    dispatch(actions.externalFile(type, fileReference, name));
  }, [onFilesDropped, dispatch]);

  const dropzone = useDropzone({
    onDrop,
    // disabled if uploads aren't allowed, since now the whole
    // upload container allows files to be dropped onto it
    disabled: !allowUpload,
    // disable any clicks on the Dropzone container since all & any inner
    // elements bubble the event up to the parent triggering the picker
    noClick: true,
  });

  const inlineLayoutDropzone = <InlineLayout
    allowUpload={allowUpload}
    allowTemplate={allowTemplate}
    isBulkSend={isBulkSend}
    allowBulkSend={allowBulkSend}
    allowIntegrations={allowIntegrations}
    onClickAddTemplateButton={makeToggleTemplatePickerCallbackFunction(true)}
    toggleIntegrationPicker={toggleIntegrationPicker}
    integrations={integrations}
    dropzone={dropzone} />;

  const menuLayoutDropzone = <MenuLayout
    allowUpload={allowUpload}
    allowTemplate={allowTemplate}
    allowIntegrations={allowIntegrations}
    onClickAddTemplateButton={makeToggleTemplatePickerCallbackFunction(true)}
    toggleIntegrationPicker={toggleIntegrationPicker}
    integrations={integrations}
    dropzone={dropzone}
    isUploading={isUploading}
    isFirstTimeExperience={isFirstTimeExperience}
    hideMenuLayoutSignerGraphic={hideMenuLayoutSignerGraphic} />;

  return (
    <>
      {
        displayType === types.DropzoneDisplayTypes.MenuLayout
          ? menuLayoutDropzone
          : inlineLayoutDropzone
      }

      {showRedesignedModal && (
        <SelectTemplateModal
          isOpen={templatePickerStatus}
          onRequestClose={makeToggleTemplatePickerCallbackFunction(false)}
          title={
            <FormattedMessage
              id=""
              description="Modal title"
              defaultMessage="Add a template" />
          }
          userCanCreateTemplates={userCanCreateEditTemplates}
          onSelected={(UnifiedJSONTemplate) => {
            onSelectTemplate({
              id: UnifiedJSONTemplate.guid,
            });
          }} />
      )}

      {!showRedesignedModal && (
        <TemplatePicker
          isOpen={templatePickerStatus}
          togglePicker={makeToggleTemplatePickerCallbackFunction}
          onSelectTemplate={onSelectTemplate}
          filter={filter} />
      )}

      {allowIntegrations && (
        <>
          {integrations && integrations[types.UploadIntegrationType.Dropbox] && (
            <DropboxFilePicker
              // The condition to render this should guarantee that this is some value
              settings={integrations[types.UploadIntegrationType.Dropbox]!}
              shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.Dropbox]}
              onSuccess={(fileReference, name) => onSelectExternalFile(
                types.UploadIntegrationType.Dropbox,
                fileReference,
                name,
              )}
              toggleIntegrationPicker={toggleIntegrationPicker} />
          )}

          {integrations && integrations[types.UploadIntegrationType.GoogleDrive] && (
            <GoogleDriveFilePicker
              // The condition to render this should guarantee that this is some value
              settings={integrations[types.UploadIntegrationType.GoogleDrive]!}
              shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.GoogleDrive]}
              onSuccess={(fileReference, name) => onSelectExternalFile(
                types.UploadIntegrationType.GoogleDrive,
                fileReference,
                name,
              )}
              toggleIntegrationPicker={toggleIntegrationPicker} />
          )}

          {integrations && integrations[types.UploadIntegrationType.Box] && (
            <BoxFilePicker
              // The condition to render this should guarantee that this is some value
              settings={integrations[types.UploadIntegrationType.Box]!}
              shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.Box]}
              onSuccess={(fileReference, name) => onSelectExternalFile(
                types.UploadIntegrationType.Box,
                fileReference,
                name,
              )}
              toggleIntegrationPicker={toggleIntegrationPicker} />
          )}

          {integrations && integrations[types.UploadIntegrationType.Evernote] && (
            <EvernoteFilePicker
              // The condition to render this should guarantee that this is some value
              settings={integrations[types.UploadIntegrationType.Evernote]!}
              shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.Evernote]}
              onSuccess={(fileReference, name) => onSelectExternalFile(
                types.UploadIntegrationType.Evernote,
                fileReference,
                name,
              )}
              toggleIntegrationPicker={toggleIntegrationPicker} />
          )}

          {integrations && integrations[types.UploadIntegrationType.OneDrive] && (
            <OneDriveFilePicker
              // The condition to render this should guarantee that this is some value
              settings={integrations[types.UploadIntegrationType.OneDrive]!}
              shouldTriggerPicker={uploadIntegration[types.UploadIntegrationType.OneDrive]}
              onSuccess={(fileReference, name) => onSelectExternalFile(
                types.UploadIntegrationType.OneDrive,
                fileReference,
                name,
              )}
              toggleIntegrationPicker={toggleIntegrationPicker} />
          )}
        </>
      )}
    </>
  );
};

export default Dropzone;
